import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Copy,
  FileText,
  Layers,
  Minus,
  Plus,
  Sparkles,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "~/components/ui/tooltip";
import { useProjectContext } from "~/components/layout/project-context";
import { pipelineStatsQueryOptions } from "~/lib/pipeline/queries";
import { projectOverviewQueryOptions } from "~/lib/project-overview/queries";
import type { PipelineStage } from "~/lib/pipeline/mock-data";
import type { ProjectMetrics, TeamMember, UserProgress } from "~/lib/project-overview/functions";

export const Route = createFileRoute("/_auth/projects/$projectId/")({
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(pipelineStatsQueryOptions(params.projectId)),
      context.queryClient.ensureQueryData(projectOverviewQueryOptions(params.projectId)),
    ]);
  },
  component: ProjectDetailPage,
});

function ProjectDetailPage() {
  const {
    projectId,
    projectTitle,
    projectDescription,
  } = useProjectContext();
  const { data: stages } = useSuspenseQuery(pipelineStatsQueryOptions(projectId));
  const { data: overview } = useSuspenseQuery(projectOverviewQueryOptions(projectId));

  const metrics = overview.metrics;
  const totalScreened = metrics.included + metrics.excluded;
  const screeningProgress =
    metrics.totalCitations > 0
      ? Math.round((totalScreened / metrics.totalCitations) * 100)
      : 0;

  const importStage = stages.find((s) => s.id === "import");
  const duplicatesRemoved = importStage
    ? Math.max(0, importStage.stats.total - metrics.totalCitations)
    : 0;

  // Empty state
  if (metrics.totalCitations === 0) {
    return (
      <div className="flex min-h-screen flex-col">
        <EmptyState
          projectId={projectId}
          projectTitle={projectTitle}
          projectDescription={projectDescription}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-6 py-8">
          {/* Header */}
          <header className="animate-fade-in-up mb-10">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
              <div className="max-w-xl">
                <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                  {projectTitle}
                </h1>
                {projectDescription && (
                  <p className="text-muted-foreground mt-2 text-balance leading-relaxed">
                    {projectDescription}
                  </p>
                )}
              </div>

              {/* Team Avatars */}
              <TeamAvatars team={overview.team} />
            </div>
          </header>

          {/* Stats Grid */}
          <div className="animate-fade-in-up-delayed stagger-1 mb-8">
            <StatsGrid
              metrics={metrics}
              screeningProgress={screeningProgress}
              totalScreened={totalScreened}
              duplicatesRemoved={duplicatesRemoved}
              projectId={projectId}
            />
          </div>

          {/* Secondary Badges */}
          {(duplicatesRemoved > 0 || metrics.conflicts > 0 || metrics.pending > 0) && (
            <div className="animate-fade-in-up-delayed stagger-2 mb-10 flex flex-wrap items-center gap-2">
              {metrics.pending > 0 && (
                <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-xs">
                  <Sparkles className="size-3" />
                  {metrics.pending.toLocaleString()} pending
                </Badge>
              )}
              {duplicatesRemoved > 0 && (
                <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-xs">
                  <Copy className="size-3" />
                  {duplicatesRemoved.toLocaleString()} duplicates removed
                </Badge>
              )}
              {metrics.conflicts > 0 && (
                <Badge
                  variant="secondary"
                  className="gap-1.5 border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-600 dark:text-amber-400"
                  render={<Link to="/projects/$projectId/duplicates" params={{ projectId }} />}
                >
                  <Layers className="size-3" />
                  {metrics.conflicts} to resolve
                </Badge>
              )}
            </div>
          )}

          {/* Work Section */}
          <div className="animate-fade-in-up-delayed stagger-3">
            <WorkSection
              stages={stages}
              metrics={metrics}
              userProgress={overview.userProgress}
              projectId={projectId}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

// ============================================================================
// Empty State
// ============================================================================

function EmptyState({
  projectId,
  projectTitle,
  projectDescription,
}: {
  projectId: string;
  projectTitle: string;
  projectDescription?: string | null;
}) {
  return (
    <main className="flex flex-1 items-center justify-center p-6">
      <div className="animate-fade-in-up max-w-md text-center">
        <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-2xl bg-muted">
          <FileText className="size-7 text-muted-foreground" />
        </div>

        <h1 className="text-xl font-semibold tracking-tight">{projectTitle}</h1>
        {projectDescription && (
          <p className="text-muted-foreground mx-auto mt-2 max-w-sm text-sm text-balance">
            {projectDescription}
          </p>
        )}

        <div className="mt-8">
          <Button
            render={<Link to="/projects/$projectId/import" params={{ projectId }} />}
          >
            Import citations
            <ArrowUpRight className="size-4" />
          </Button>
          <p className="text-muted-foreground mt-3 text-xs">
            Import your first citations to begin
          </p>
        </div>
      </div>
    </main>
  );
}

// ============================================================================
// Team Avatars
// ============================================================================

function TeamAvatars({ team }: { team: TeamMember[] }) {
  const activeMembers = team.filter((m) => m.screened > 0).slice(0, 5);
  const remainingCount = Math.max(0, team.filter((m) => m.screened > 0).length - 5);

  if (activeMembers.length === 0) return null;

  return (
    <TooltipProvider>
      <div className="flex items-center gap-3">
        <div className="flex -space-x-2">
          {activeMembers.map((member) => (
            <Tooltip key={member.id}>
              <TooltipTrigger asChild>
                <Avatar className="ring-background size-8 ring-2">
                  {member.image && (
                    <AvatarImage src={member.image} alt={member.name} />
                  )}
                  <AvatarFallback className="text-[10px] font-medium">
                    {getInitials(member.name)}
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                <p className="font-medium">{member.name}</p>
                <p className="text-muted-foreground">{member.screened.toLocaleString()} screened</p>
              </TooltipContent>
            </Tooltip>
          ))}
          {remainingCount > 0 && (
            <div className="ring-background flex size-8 items-center justify-center rounded-full bg-muted text-xs font-medium ring-2">
              +{remainingCount}
            </div>
          )}
        </div>
        <span className="text-muted-foreground text-xs">
          {team.filter((m) => m.screened > 0).length} active
        </span>
      </div>
    </TooltipProvider>
  );
}

// ============================================================================
// Stats Grid
// ============================================================================

function StatsGrid({
  metrics,
  screeningProgress,
  totalScreened,
  duplicatesRemoved,
  projectId,
}: {
  metrics: ProjectMetrics;
  screeningProgress: number;
  totalScreened: number;
  duplicatesRemoved: number;
  projectId: string;
}) {
  return (
    <div className="grid gap-px overflow-hidden rounded-xl border bg-border sm:grid-cols-2 lg:grid-cols-4">
      {/* Total Citations */}
      <div className="bg-card p-5">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
            Total
          </span>
          <FileText className="size-4 text-muted-foreground/50" />
        </div>
        <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight">
          {metrics.totalCitations.toLocaleString()}
        </p>
        <p className="text-muted-foreground mt-1 text-xs">citations imported</p>
      </div>

      {/* Screening Progress */}
      <div className="bg-card p-5">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
            Progress
          </span>
          <span className="text-xs font-medium tabular-nums">{screeningProgress}%</span>
        </div>
        <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight">
          {totalScreened.toLocaleString()}
        </p>
        <div className="mt-2">
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-foreground transition-all duration-700 ease-out"
              style={{ width: `${screeningProgress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Included */}
      <div className="bg-card p-5">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
            Included
          </span>
          <Plus className="size-4 text-emerald-500" />
        </div>
        <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-emerald-600 dark:text-emerald-400">
          {metrics.included.toLocaleString()}
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          {metrics.totalCitations > 0
            ? `${Math.round((metrics.included / metrics.totalCitations) * 100)}% of total`
            : "—"}
        </p>
      </div>

      {/* Excluded */}
      <div className="bg-card p-5">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
            Excluded
          </span>
          <Minus className="size-4 text-rose-500" />
        </div>
        <p className="mt-2 text-3xl font-semibold tabular-nums tracking-tight text-rose-600 dark:text-rose-400">
          {metrics.excluded.toLocaleString()}
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          {metrics.totalCitations > 0
            ? `${Math.round((metrics.excluded / metrics.totalCitations) * 100)}% of total`
            : "—"}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Work Section
// ============================================================================

function WorkSection({
  stages,
  metrics,
  userProgress,
  projectId,
}: {
  stages: PipelineStage[];
  metrics: ProjectMetrics;
  userProgress: UserProgress;
  projectId: string;
}) {
  const titleAbstractStage = stages.find((s) => s.id === "title_abstract");
  const fullTextStage = stages.find((s) => s.id === "full_text");

  const hasDuplicates = metrics.conflicts > 0;
  const hasTitleAbstractWork =
    titleAbstractStage?.status === "active" && userProgress.remaining > 0;
  const hasFullTextWork =
    fullTextStage?.status === "active" && (fullTextStage.userStats?.remaining ?? 0) > 0;

  const hasWork = hasDuplicates || hasTitleAbstractWork || hasFullTextWork;
  const userTotal = userProgress.screened + userProgress.remaining;
  const userProgressPercent =
    userTotal > 0 ? Math.round((userProgress.screened / userTotal) * 100) : 0;

  return (
    <Card>
      <CardContent className="pt-0">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Your Work</h2>
            {userProgress.screened > 0 && (
              <p className="text-muted-foreground mt-0.5 text-sm">
                {userProgress.screened.toLocaleString()} screened
                {userProgress.remaining > 0 && (
                  <span className="text-foreground"> · {userProgress.remaining.toLocaleString()} remaining</span>
                )}
              </p>
            )}
          </div>
          {userTotal > 0 && (
            <div className="text-right">
              <span className="text-2xl font-semibold tabular-nums">{userProgressPercent}%</span>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {userTotal > 0 && (
          <div className="mb-8 h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-foreground transition-all duration-700 ease-out"
              style={{ width: `${userProgressPercent}%` }}
            />
          </div>
        )}

        {/* Work Queue */}
        {hasWork ? (
          <div className="space-y-2">
            {hasDuplicates && (
              <WorkCard
                title="Resolve duplicates"
                count={metrics.conflicts}
                unit={metrics.conflicts === 1 ? "group" : "groups"}
                to="/projects/$projectId/duplicates"
                projectId={projectId}
                variant="warning"
              />
            )}
            {hasTitleAbstractWork && (
              <WorkCard
                title="Title & Abstract Screening"
                count={userProgress.remaining}
                unit="citations"
                to="/projects/$projectId/screening/title-abstract/review"
                projectId={projectId}
              />
            )}
            {hasFullTextWork && (
              <WorkCard
                title="Full-Text Review"
                count={fullTextStage!.userStats!.remaining}
                unit="articles"
                to="/projects/$projectId/screening/full-text/review"
                projectId={projectId}
              />
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-emerald-500/10">
              <Check className="size-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="font-medium">All caught up</p>
            {userProgress.screened > 0 && (
              <p className="text-muted-foreground mt-1 text-sm">
                You've screened {userProgress.screened.toLocaleString()} citations
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WorkCard({
  title,
  count,
  unit,
  to,
  projectId,
  variant = "default",
}: {
  title: string;
  count: number;
  unit: string;
  to: string;
  projectId: string;
  variant?: "default" | "warning";
}) {
  return (
    <Link
      to={to}
      params={{ projectId }}
      className={`group flex items-center justify-between gap-4 rounded-lg border p-4 transition-all hover:bg-accent/50 ${
        variant === "warning"
          ? "border-amber-500/20 bg-amber-500/5"
          : "border-border"
      }`}
    >
      <div className="min-w-0">
        <p
          className={`font-medium ${
            variant === "warning" ? "text-amber-600 dark:text-amber-400" : ""
          }`}
        >
          {title}
        </p>
        <p className="text-muted-foreground text-sm">
          <span className="font-medium tabular-nums">{count.toLocaleString()}</span> {unit}
        </p>
      </div>
      <Button size="sm" variant="ghost" className="shrink-0 gap-1.5">
        Continue
        <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
      </Button>
    </Link>
  );
}

// ============================================================================
// Utilities
// ============================================================================

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}
